import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useRef } from "react";
import { useLinkMicrosoftOAuthMutation } from "../api/queries/userQueries";
import { useAuth } from "../hooks/useAuth";
import { completeOAuthLogin } from "../utils/completeOAuthLogin";
import { formatMicrosoftOAuthError } from "../utils/microsoftOAuthErrors";
import PageLoading from "./PageLoading";

function isMicrosoftLoginFlow(): boolean {
  return (
    sessionStorage.getItem("oauth_provider") === "microsoft" &&
    sessionStorage.getItem("oauth_reconnect") !== "true"
  );
}

const MicrosoftOAuthRedirect = () => {
  const hasRun = useRef(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  const linkMicrosoftOAuthMutation = useLinkMicrosoftOAuthMutation();
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");
  const state = searchParams.get("state");
  const error = searchParams.get("error");
  const errorDescription = searchParams.get("error_description");

  useEffect(() => {
    // Prevent the useEffect from running twice in dev due to StrictMode
    if (hasRun.current) return;
    hasRun.current = true;

    if (error) {
      console.error("Error linking Microsoft OAuth", error, errorDescription);
      if (isMicrosoftLoginFlow()) {
        sessionStorage.removeItem("oauth_provider");
        navigate("/login", {
          replace: true,
          state: {
            oauthError: formatMicrosoftOAuthError(
              errorDescription,
              error === "access_denied"
                ? "Microsoft sign-in was cancelled or denied. Please try again."
                : "Microsoft sign-in failed. Please try again.",
            ),
          },
        });
        return;
      }
      navigate("/settings");
      return;
    }

    if (!code) {
      if (isMicrosoftLoginFlow()) {
        sessionStorage.removeItem("oauth_provider");
        navigate("/login", {
          replace: true,
          state: {
            oauthError: "Microsoft sign-in did not return an authorization code.",
          },
        });
      }
      return;
    }

    const dedupeKey = `oauth_code_used_${code}`;
    if (sessionStorage.getItem(dedupeKey)) {
      return;
    }
    sessionStorage.setItem(dedupeKey, "1");

    const handleRedirect = async () => {
      if (isMicrosoftLoginFlow()) {
        try {
          await completeOAuthLogin({
            provider: "microsoft",
            code,
            login,
            navigate,
          });
        } catch (loginError) {
          console.error("Microsoft OAuth login failed:", loginError);
          sessionStorage.removeItem("oauth_provider");
          sessionStorage.removeItem(dedupeKey);
          const detail = (loginError as { response?: { data?: { detail?: unknown } } })
            ?.response?.data?.detail;
          const oauthError =
            typeof detail === "string"
              ? detail
              : typeof detail === "object" &&
                  detail !== null &&
                  "message" in detail &&
                  typeof (detail as { message?: unknown }).message === "string"
                ? (detail as { message: string }).message
                : "Microsoft sign-in failed. Please try again.";
          navigate("/login", {
            replace: true,
            state: { oauthError },
          });
        }
        return;
      }

      try {
        await linkMicrosoftOAuthMutation.mutateAsync({
          code,
          state: state ?? "",
        });
      } catch (linkError) {
        console.error("Error linking Microsoft OAuth", linkError);
      }
      sessionStorage.removeItem("oauth_provider");
      sessionStorage.removeItem("oauth_reconnect");
      navigate("/settings");
    };

    void handleRedirect();
    // P.S. Due to StrictMode, this component will unmount and remount.
    // Don't abort the in-flight mutation on unmount; it causes cancellations in StrictMode.
    return () => {};
  }, [
    code,
    error,
    errorDescription,
    linkMicrosoftOAuthMutation,
    login,
    navigate,
    state,
  ]);

  return <PageLoading />;
};

export default MicrosoftOAuthRedirect;
